import { useCallback, useEffect, useRef, useState } from 'react';
import type { AudioProgressEvents } from '../types';

export interface UseAudioPlaybackOptions {
  /**
   * Call `play()` as soon as the element is created (see
   * `PodcastProps.autoPlay`). Read once, on mount: auto-play is an "on open"
   * decision, so a later change must neither restart the track nor tear the
   * element down.
   */
  autoPlay?: boolean;
  /** Position (seconds) to start from. Read once, on mount, like `autoPlay`. */
  initialTime?: number;
  /** Playback speed to start with. Read once, on mount, like `autoPlay`. */
  initialSpeed?: number;
  /** Progress callbacks, held in a ref like `onTrackEnd`. */
  progressEvents?: AudioProgressEvents;
}

/**
 * Drives a single `HTMLAudioElement` for the given url. Callers remount
 * this hook's owner (via a `key`) when the url changes, so the effect
 * only ever subscribes to one element's lifecycle — it doesn't need to
 * reset state itself.
 *
 * `onTrackEnd` (optional) fires when the track plays to its end, before the
 * playhead resets — held in a ref so a changing callback never resubscribes
 * the audio element.
 */
export const useAudioPlayback = (
  url: string,
  onTrackEnd?: () => void,
  {
    autoPlay = false,
    initialTime,
    initialSpeed,
    progressEvents,
  }: UseAudioPlaybackOptions = {},
) => {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const rafRef = useRef(0);
  const [playing, setPlaying] = useState(false);
  // Sound really comes out: false while the mp3 loads, buffers or failed.
  const [audible, setAudible] = useState(false);
  const [currentTime, setCurrentTime] = useState(initialTime ?? 0);
  const [duration, setDuration] = useState(0);

  const onTrackEndRef = useRef(onTrackEnd);
  useEffect(() => {
    onTrackEndRef.current = onTrackEnd;
  }, [onTrackEnd]);

  const progressEventsRef = useRef(progressEvents);
  useEffect(() => {
    progressEventsRef.current = progressEvents;
  }, [progressEvents]);

  // Mount-time snapshots, deliberately never updated (see the options' doc).
  const autoPlayRef = useRef(autoPlay);
  const initialRef = useRef({ time: initialTime, speed: initialSpeed });

  // Audio seconds really played; `last` is null right after a seek.
  const listenedRef = useRef<{
    total: number;
    reported: number;
    last: number | null;
  }>({ total: 0, reported: 0, last: 0 });

  useEffect(() => {
    const audio = new window.Audio(url);
    audioRef.current = audio;

    const { time: startTime, speed: startSpeed } = initialRef.current;
    if (startSpeed) {
      // Both, or Safari re-applies the default rate on play.
      audio.defaultPlaybackRate = startSpeed;
      audio.playbackRate = startSpeed;
    }

    let restored = false;
    // Until the start position is applied, it is still the position.
    const position = () =>
      restored || !startTime ? audio.currentTime : startTime;

    const reportProgress = () =>
      progressEventsRef.current?.onProgress?.(position(), audio.playbackRate);
    const reportLeave = () =>
      progressEventsRef.current?.onLeave?.(position(), audio.playbackRate);

    const onLoadedMetadata = () => {
      setDuration(audio.duration || 0);
      // Seeking needs the metadata; reloads of the metadata must not seek again.
      if (startTime && !restored) {
        listenedRef.current.last = null;
        audio.currentTime = startTime;
      }
      restored = true;
    };
    const onTimeUpdate = () => {
      const listened = listenedRef.current;
      const now = audio.currentTime;
      if (listened.last !== null && now > listened.last) {
        listened.total += now - listened.last;
      }
      listened.last = now;
      // rAF doesn't run in background tabs; keep the position fresh there.
      setCurrentTime(position());
    };
    // Any seek (bar, skip, restore, replay after the end) isn't listening.
    const onSeeking = () => {
      listenedRef.current.last = null;
    };
    const onPlay = () => {
      setPlaying(true);
      progressEventsRef.current?.onPlay?.();
    };
    const onPlaying = () => setAudible(true);
    const onSilent = () => setAudible(false);
    const onPause = () => {
      setPlaying(false);
      setAudible(false);
      reportProgress();
    };
    const onEnded = () => {
      onTrackEndRef.current?.();
      progressEventsRef.current?.onComplete?.(audio.playbackRate);
      setPlaying(false);
      setAudible(false);
      setCurrentTime(0);
    };
    audio.addEventListener('loadedmetadata', onLoadedMetadata);
    audio.addEventListener('play', onPlay);
    audio.addEventListener('playing', onPlaying);
    audio.addEventListener('waiting', onSilent);
    audio.addEventListener('error', onSilent);
    audio.addEventListener('pause', onPause);
    audio.addEventListener('ended', onEnded);
    audio.addEventListener('ratechange', reportProgress);
    audio.addEventListener('timeupdate', onTimeUpdate);
    audio.addEventListener('seeking', onSeeking);
    window.addEventListener('pagehide', reportLeave);

    if (autoPlayRef.current) {
      // Rejected when no user gesture preceded it (browser autoplay policy):
      // the player then stays paused, exactly as a failed toggle() would.
      audio.play().catch(() => undefined);
    }

    function tick() {
      setCurrentTime(position());
      rafRef.current = requestAnimationFrame(tick);
    }
    rafRef.current = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(rafRef.current);
      // Before pausing: the pause event would arrive after its listener is gone.
      reportLeave();
      audio.pause();
      audio.removeEventListener('loadedmetadata', onLoadedMetadata);
      audio.removeEventListener('play', onPlay);
      audio.removeEventListener('playing', onPlaying);
      audio.removeEventListener('waiting', onSilent);
      audio.removeEventListener('error', onSilent);
      audio.removeEventListener('pause', onPause);
      audio.removeEventListener('ended', onEnded);
      audio.removeEventListener('ratechange', reportProgress);
      audio.removeEventListener('timeupdate', onTimeUpdate);
      audio.removeEventListener('seeking', onSeeking);
      window.removeEventListener('pagehide', reportLeave);
    };
  }, [url]);

  const toggle = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.paused) {
      audio.play().catch(() => undefined);
    } else {
      audio.pause();
    }
  }, []);

  const seekTo = useCallback((time: number) => {
    const audio = audioRef.current;
    // No seeking before the metadata: the clamp would report 0 as the position.
    if (!audio?.duration) return;
    const clamped = Math.min(Math.max(time, 0), audio.duration);
    // Before `seeking` fires, a pending timeupdate could count the jump.
    listenedRef.current.last = null;
    audio.currentTime = clamped;
    setCurrentTime(clamped);
    // Reported here, not on `seeked`: only visitor seeks come through seekTo.
    progressEventsRef.current?.onSeek?.(clamped, audio.playbackRate);
  }, []);

  const skip = useCallback(
    (seconds: number) => {
      const audio = audioRef.current;
      if (!audio) return;
      seekTo(audio.currentTime + seconds);
    },
    [seekTo],
  );

  const [playbackRateState, setPlaybackRateState] = useState(
    initialSpeed ?? 1,
  );
  const setPlaybackRate = useCallback((rate: number) => {
    const audio = audioRef.current;
    if (!audio) return;
    // Keep in sync with the restored default, or a reload reverts to it.
    audio.defaultPlaybackRate = rate;
    audio.playbackRate = rate;
    setPlaybackRateState(rate);
  }, []);

  /** Whole audio seconds played since the previous call; the rest carries over. */
  const takeListenedSeconds = useCallback(() => {
    const listened = listenedRef.current;
    const seconds = Math.floor(listened.total) - listened.reported;
    listened.reported += seconds;
    return seconds;
  }, []);

  return {
    playing,
    audible,
    currentTime,
    duration,
    toggle,
    seekTo,
    skip,
    playbackRate: playbackRateState,
    setPlaybackRate,
    takeListenedSeconds,
  };
};
